/* eslint-disable no-undef */
class CarouselComponent extends HTMLElement {
  constructor () {
    super()
    this.handleResize = debounce(this.handleResize.bind(this), 200)
    this.plugins = []
    this.prevBtnHandler = null
    this.nextBtnHandler = null
    this.prevBtnEl = null
    this.nextBtnEl = null
    this.lastKnownWidth = window.innerWidth
    this.carouselControls = this.querySelector('.carousel-controls')
    this.hasAnimatedNext = false
    this.intersectionObserver = null
    this.previousSlideIndex = 0
    this.isAnimatingDots = false
    this.externalEventHandlers = {}
    this.dotsSelectListenerAdded = false
  }

  connectedCallback () {
    this.initCarousel()
    window.addEventListener('resize', this.handleResize)
    this.updateControlsVisibility()

    // Add intersection observer if animation is enabled
    if (this.animateNextOnShow) {
      this.setupIntersectionObserver()
    }

    // External control events
    this.externalEventHandlers.next = (e) => {
      if (this.embla) this.embla.scrollNext({ jump: true })
    }
    this.externalEventHandlers.prev = (e) => {
      if (this.embla) this.embla.scrollPrev({ jump: true })
    }
    this.externalEventHandlers.scrollTo = (e) => {
      const idx = typeof e.detail?.index === 'number' ? e.detail.index : null
      if (this.embla && idx !== null) this.embla.scrollTo(idx, e.detail?.jump === false ? undefined : { jump: true })
    }
    this.externalEventHandlers.reinit = () => {
      if (!this.embla) {
        this.initCarousel()
        return
      }
      if (typeof this.embla.reInit === 'function') {
        this.embla.reInit()
      }
      if (this.hasDots) {
        if (!this.dotsSelectListenerAdded) {
          this.embla.on('select', () => {
            const currentIndex = this.embla.selectedScrollSnap()
            const slideJump = Math.abs(currentIndex - this.previousSlideIndex)

            if (slideJump > 1) {
              this.animateDotsTransition(this.previousSlideIndex, currentIndex)
            } else {
              this.updateDots()
            }

            this.previousSlideIndex = currentIndex
          })
          this.dotsSelectListenerAdded = true
        }
        this.setupDots()
      }
      this.dispatchSlideChangeEvent()
    }
    this.addEventListener('carousel:next', this.externalEventHandlers.next)
    this.addEventListener('carousel:prev', this.externalEventHandlers.prev)
    this.addEventListener('carousel:scrollTo', this.externalEventHandlers.scrollTo)
    this.addEventListener('carousel:reinit', this.externalEventHandlers.reinit)

    this.externalEventHandlers.recsLoaded = (e) => {
      const productId = e && e.detail && e.detail.productId ? String(e.detail.productId) : null
      const recsEl = this.querySelector('pdp-recommendations')
      const matches = recsEl && String(recsEl.dataset.productId) === productId
      if (!matches) return
      if (!this.embla && this.isCarouselActive()) {
        this.initCarousel()
      } else if (this.embla) {
        if (typeof this.embla.reInit === 'function') this.embla.reInit()
      }
      if (this.hasDots) this.setupDots()
      if (this.hasControls) this.updateArrowButtonsState()
      this.updateControlsVisibility()
    }
    window.addEventListener('pdp:recommendations:loaded', this.externalEventHandlers.recsLoaded)
  }

  disconnectedCallback () {
    window.removeEventListener('resize', this.handleResize)
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
    this.destroyCarousel()

    // Remove external control events
    if (this.externalEventHandlers.next) this.removeEventListener('carousel:next', this.externalEventHandlers.next)
    if (this.externalEventHandlers.prev) this.removeEventListener('carousel:prev', this.externalEventHandlers.prev)
    if (this.externalEventHandlers.scrollTo) this.removeEventListener('carousel:scrollTo', this.externalEventHandlers.scrollTo)
    if (this.externalEventHandlers.reinit) this.removeEventListener('carousel:reinit', this.externalEventHandlers.reinit)
    if (this.externalEventHandlers.recsLoaded) window.removeEventListener('pdp:recommendations:loaded', this.externalEventHandlers.recsLoaded)
  }

  initCarousel () {
    if (!this.carousel || !this.isCarouselActive()) {
      this.updateControlsVisibility()
      this.hideDotsWhenInactive()
      return
    }

    if (this.pluginType !== 'none') {
      this.loadPluginsAndInit()
      return
    }
    this.setupCarousel()
  }

  loadPluginsAndInit () {
    const pluginPromises = []

    if (this.shouldInitPlugin()) {
      if (this.pluginType === 'autoscroll') {
        pluginPromises.push(
          new Promise(resolve => {
            window.Tentbox.loadScript(window.Tentbox.carousel.plugins.autoscrollSrc, resolve)
          }),
        )
      }

      if (this.pluginType === 'autoplay') {
        pluginPromises.push(
          new Promise(resolve => {
            window.Tentbox.loadScript(window.Tentbox.carousel.plugins.autoplaySrc, resolve)
          }),
        )
      }
    }

    Promise.all(pluginPromises).then(() => {
      this.setupCarousel()
    })
  }

  setupCarousel () {
    if (this.embla) {
      this.destroyCarousel()
    }

    const options = this.options

    if (this.pluginType === 'none') {
      this.embla = EmblaCarousel(this.carousel, options)
    }

    if (this.hasControls) {
      const prevBtn = this.querySelector('.carousel-controls__button--previous')
      const nextBtn = this.querySelector('.carousel-controls__button--next')

      // Remove existing event listeners if they exist
      if (this.prevBtnHandler) {
        prevBtn.removeEventListener('click', this.prevBtnHandler)
      }
      if (this.nextBtnHandler) {
        nextBtn.removeEventListener('click', this.nextBtnHandler)
      }

      // Define new event handler functions
      this.prevBtnHandler = () => this.embla.scrollPrev({ jump: true })
      this.nextBtnHandler = () => this.embla.scrollNext({ jump: true })

      // Add new event listeners
      prevBtn.addEventListener('click', this.prevBtnHandler, false)
      nextBtn.addEventListener('click', this.nextBtnHandler, false)

      this.prevBtnEl = prevBtn
      this.nextBtnEl = nextBtn
    }

    if (this.pluginType !== 'none') {
      const plugins = []

      if (this.shouldInitPlugin()) {
        if (this.pluginType === 'autoscroll') {
          plugins.push(EmblaCarouselAutoScroll({ speed: this.pluginSpeedForAutoscroll }))
          this.addMouseEventsForAutoscroll()
        }

        if (this.pluginType === 'autoplay') {
          plugins.push(EmblaCarouselAutoplay({ delay: this.pluginSpeedForAutoplay, playOnInit: true }))
          this.addMouseEventsForAutoplay()
        }
      }

      this.embla = EmblaCarousel(this.carousel, options, plugins)
      this.plugins = plugins
    }

    if (this.hasDots) {
      this.embla.on('select', () => {
        const currentIndex = this.embla.selectedScrollSnap()
        const slideJump = Math.abs(currentIndex - this.previousSlideIndex)

        if (slideJump > 1) {
          this.animateDotsTransition(this.previousSlideIndex, currentIndex)
        } else {
          this.updateDots()
        }

        this.previousSlideIndex = currentIndex
      })
      this.dotsSelectListenerAdded = true

      this.setupDots()
    }

    if (this.hasControls) {
      this.embla.on('select', () => {
        this.updateArrowButtonsState()
      })

      this.updateArrowButtonsState()
    }

    this.embla.on('select', () => this.dispatchSlideChangeEvent())
    this.dispatchSlideChangeEvent()
  }

  dispatchSlideChangeEvent () {
    if (!this.embla) return

    const selectedIndex = this.embla.selectedScrollSnap()
    const totalSlides = this.embla.slideNodes().length
    const container = this.embla.containerNode()
    const slides = this.embla.slideNodes()
    let slidesVisible = 1

    if (container && slides.length > 0) {
      const containerWidth = container.getBoundingClientRect().width
      const slideWidth = slides[0].getBoundingClientRect().width
      slidesVisible = slideWidth > 0 ? Math.floor(containerWidth / slideWidth) : 1
    }

    const lastMeaningfulIndex = Math.max(0, totalSlides - slidesVisible)
    let isAtEnd
    let isAtStart
    if (typeof this.embla.canScrollNext === 'function' && typeof this.embla.canScrollPrev === 'function') {
      isAtEnd = !this.embla.canScrollNext()
      isAtStart = !this.embla.canScrollPrev()
    } else {
      isAtEnd = selectedIndex >= lastMeaningfulIndex
      isAtStart = selectedIndex === 0
    }

    this.dispatchEvent(new CustomEvent('carousel:slidechange', {
      bubbles: true,
      detail: { currentIndex: selectedIndex, isAtEnd, isAtStart, totalSlides, slidesVisible },
    }))
  }

  destroyCarousel () {
    if (this.embla) {
      this.plugins.forEach(plugin => {
        if (plugin && typeof plugin.destroy === 'function') {
          plugin.destroy()
        }
      })
      this.embla.destroy()
      this.dotsSelectListenerAdded = false
    }
  }

  addMouseEventsForAutoscroll () {
    this.carousel.addEventListener('mouseenter', () => {
      this.embla.plugins().autoScroll.stop()
    })

    this.carousel.addEventListener('mouseleave', () => {
      this.embla.plugins().autoScroll.play()
    })
  }

  addMouseEventsForAutoplay () {
    const mouseTarget = this.hasDots ? this : this.carousel
    mouseTarget.addEventListener('mouseenter', () => {
      this.embla.plugins().autoplay.stop()
    })

    mouseTarget.addEventListener('mouseleave', () => {
      this.embla.plugins().autoplay.play()
    })
  }

  handleResize () {
    const currentWidth = window.innerWidth

    if (this.lastKnownWidth !== currentWidth) {
      this.lastKnownWidth = currentWidth

      const shouldInitCarousel = this.isCarouselActive()

      if (!this.embla && shouldInitCarousel) {
        this.initCarousel()
      } else if (this.embla && !shouldInitCarousel) {
        this.destroyCarousel()
        this.hideDotsWhenInactive()
      } else if (this.embla && this.shouldInitPlugin()) {
        this.loadPluginsAndInit()
      } else if (this.embla && this.hasDots) {
        // Recalculate dots for dynamic behavior
        this.setupDots()
      }

      this.updateControlsVisibility()
    }
  }

  setupDots () {
    const dotsContainer = this.dotsContainer
    if (!dotsContainer) return

    // Check if this is a carousel-dots container (pre-rendered HTML)
    const isCarouselDots = dotsContainer.classList.contains('carousel-dots')

    if (isCarouselDots) {
      // For pre-rendered carousel-dots, just add click handlers to existing dots
      this.setupCarouselDots()
    } else {
      // For dynamic carousel__dots, create dots programmatically
      dotsContainer.innerHTML = ''
      const scrollPositions = this.calculateScrollPositions()

      // Hide dots if no scrolling is needed
      if (scrollPositions <= 1) {
        dotsContainer.style.display = 'none'
        return
      }

      dotsContainer.style.display = 'flex'

      for (let i = 0; i < scrollPositions; i++) {
        const dot = document.createElement('button')
        dot.setAttribute('type', 'button')
        dot.classList.add('carousel__dot')
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`)
        dot.addEventListener('click', () => {
          const targetSlideIndex = this.mapDotIndexToSlideIndex(i)
          this.embla.scrollTo(targetSlideIndex)
          this.updateDots()
        })
        dotsContainer.appendChild(dot)
      }
    }

    this.updateDots() // Initial update for correct dot highlighting
  }

  calculateScrollPositions () {
    if (!this.embla) return 0

    const totalSlides = this.embla.slideNodes().length
    const container = this.embla.containerNode()
    const slides = this.embla.slideNodes()

    if (!container || slides.length === 0) return totalSlides

    // Calculate how many slides are visible at once
    const containerWidth = container.getBoundingClientRect().width
    const slideWidth = slides[0].getBoundingClientRect().width
    const slidesVisible = Math.floor(containerWidth / slideWidth)

    // If all slides fit in viewport, no scrolling is needed
    if (slidesVisible >= totalSlides) {
      return 0
    }

    // If only one slide is visible (mobile), each slide gets its own position
    if (slidesVisible <= 1) {
      return totalSlides
    }

    // Calculate number of scroll positions
    // This is the number of positions where the carousel can stop
    // Formula: totalSlides - slidesVisible + 1
    return Math.max(1, totalSlides - slidesVisible + 1)
  }

  setupCarouselDots () {
    const dots = this.dotsContainer.querySelectorAll('.carousel__dot')
    const scrollPositions = this.calculateScrollPositions()

    // Hide entire dots container if no scrolling is needed
    if (scrollPositions <= 1) {
      this.dotsContainer.style.display = 'none'
      return
    }

    this.dotsContainer.style.display = 'flex'

    // Show/hide dots based on calculated scroll positions
    dots.forEach((dot, index) => {
      if (index < scrollPositions) {
        dot.style.display = 'block'
      } else {
        dot.style.display = 'none'
      }

      // Remove any existing click handlers
      const newDot = dot.cloneNode(true)
      dot.parentNode.replaceChild(newDot, dot)

      // Add click handler
      newDot.addEventListener('click', () => {
        if (this.embla) {
          const currentIndex = this.embla.selectedScrollSnap()
          const targetSlideIndex = this.mapDotIndexToSlideIndex(index)
          const slideJump = Math.abs(targetSlideIndex - currentIndex)

          if (slideJump > 1) {
            // Animate dots through the transition
            this.animateDotsTransition(currentIndex, targetSlideIndex)
          }

          this.embla.scrollTo(targetSlideIndex)
          this.previousSlideIndex = targetSlideIndex
        }
      })
    })
  }

  updateDots () {
    const dotsContainer = this.dotsContainer
    if (!dotsContainer || !this.embla) return

    const selectedIndex = this.embla.selectedScrollSnap()
    const isCarouselDots = dotsContainer.classList.contains('carousel-dots')

    if (isCarouselDots) {
      // Update carousel-dots active state
      const dots = dotsContainer.querySelectorAll('.carousel__dot')
      const scrollPositions = this.calculateScrollPositions()

      // Map the selected slide index to the appropriate dot index
      const dotIndex = this.mapSlideIndexToDotIndex(selectedIndex, scrollPositions)

      dots.forEach((dot, index) => {
        if (index === dotIndex) {
          dot.classList.add('active')
        } else {
          dot.classList.remove('active')
        }
      })
    } else {
      // Update carousel__dots active state (existing functionality)
      const dots = dotsContainer.children
      const scrollPositions = this.calculateScrollPositions()
      const dotIndex = this.mapSlideIndexToDotIndex(selectedIndex, scrollPositions)

      for (let i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('active', i === dotIndex)
      }
    }
  }

  hideDotsWhenInactive () {
    const dotsContainer = this.dotsContainer
    if (dotsContainer) {
      dotsContainer.style.display = 'none'
    }
  }

  mapSlideIndexToDotIndex (slideIndex, scrollPositions) {
    if (!this.embla) return slideIndex

    const totalSlides = this.embla.slideNodes().length
    const container = this.embla.containerNode()
    const slides = this.embla.slideNodes()

    if (!container || slides.length === 0) return slideIndex

    // Calculate how many slides are visible at once
    const containerWidth = container.getBoundingClientRect().width
    const slideWidth = slides[0].getBoundingClientRect().width
    const slidesVisible = Math.floor(containerWidth / slideWidth)

    // If only one slide is visible or all slides fit in viewport, return original index
    if (slidesVisible <= 1 || slidesVisible >= totalSlides) {
      return slideIndex
    }

    // Map the slide index to the appropriate dot index
    // The last scroll position should correspond to the last meaningful slide index
    const lastMeaningfulIndex = Math.max(0, totalSlides - slidesVisible)
    const maxDotIndex = scrollPositions - 1

    if (slideIndex >= lastMeaningfulIndex) {
      return maxDotIndex
    }

    return Math.min(slideIndex, maxDotIndex)
  }

  mapDotIndexToSlideIndex (dotIndex) {
    if (!this.embla) return dotIndex

    const totalSlides = this.embla.slideNodes().length
    const container = this.embla.containerNode()
    const slides = this.embla.slideNodes()

    if (!container || slides.length === 0) return dotIndex

    // Calculate how many slides are visible at once
    const containerWidth = container.getBoundingClientRect().width
    const slideWidth = slides[0].getBoundingClientRect().width
    const slidesVisible = Math.floor(containerWidth / slideWidth)

    // If only one slide is visible or all slides fit in viewport, return original index
    if (slidesVisible <= 1 || slidesVisible >= totalSlides) {
      return dotIndex
    }

    // Map the dot index back to the appropriate slide index
    const lastMeaningfulIndex = Math.max(0, totalSlides - slidesVisible)
    const maxDotIndex = this.calculateScrollPositions() - 1

    if (dotIndex >= maxDotIndex) {
      return lastMeaningfulIndex
    }

    return Math.min(dotIndex, lastMeaningfulIndex)
  }

  animateDotsTransition (fromIndex, toIndex) {
    const dotsContainer = this.dotsContainer
    if (!dotsContainer || !this.embla || this.isAnimatingDots) {
      return
    }

    // Allow animation for both carousel-dots (pre-rendered) and carousel__dots (dynamic)
    const isCarouselDots = dotsContainer.classList.contains('carousel-dots')
    const isDynamicDots = dotsContainer.classList.contains('carousel__dots')
    if (!isCarouselDots && !isDynamicDots) return

    this.isAnimatingDots = true
    const dots = dotsContainer.querySelectorAll('.carousel__dot')
    const scrollPositions = this.calculateScrollPositions()

    // Map slide indices to dot indices
    const fromDotIndex = this.mapSlideIndexToDotIndex(fromIndex, scrollPositions)
    const toDotIndex = this.mapSlideIndexToDotIndex(toIndex, scrollPositions)

    const direction = toDotIndex > fromDotIndex ? 1 : -1
    const steps = Math.abs(toDotIndex - fromDotIndex)

    // Clear any existing active states
    dots.forEach(dot => dot.classList.remove('active'))

    // Animate through each dot in sequence
    let currentStep = 0
    const animateStep = () => {
      if (currentStep <= steps) {
        const currentDotIndex = fromDotIndex + (direction * currentStep)

        // Remove active from previous step
        if (currentStep > 0) {
          const prevDotIndex = fromDotIndex + (direction * (currentStep - 1))
          if (dots[prevDotIndex]) {
            dots[prevDotIndex].classList.remove('active')
          }
        }

        // Add active to current step
        if (dots[currentDotIndex]) {
          dots[currentDotIndex].classList.add('active')
        }

        currentStep++

        // Continue animation with a small delay
        if (currentStep <= steps) {
          setTimeout(animateStep, 50) // 50ms delay between each dot
        } else {
          this.isAnimatingDots = false
        }
      } else {
        this.isAnimatingDots = false
      }
    }

    // Start the animation
    animateStep()
  }

  isCarouselActive () {
    const breakpoints = this.options.breakpoints || {}
    const viewportWidth = window.innerWidth
    let isActive = this.options.active !== false // Default to true unless explicitly set to false

    // Sort breakpoints in descending order
    const sortedBreakpoints = Object.entries(breakpoints).sort((a, b) => {
      const widthA = parseInt(a[0].match(/\d+/)[0], 10)
      const widthB = parseInt(b[0].match(/\d+/)[0], 10)
      return widthB - widthA
    })

    for (const [breakpoint, settings] of sortedBreakpoints) {
      const minWidth = parseInt(breakpoint.match(/\d+/)[0], 10)
      if (viewportWidth >= minWidth) {
        isActive = settings.active !== false
        break // Exit the loop after finding the first matching breakpoint
      }
    }

    return isActive
  }

  shouldInitPlugin () {
    const pluginFromBreakpoint = this.pluginFrom || 'all'
    const viewportWidth = window.innerWidth
    const breakpoints = {
      xs: 450,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    }

    if (pluginFromBreakpoint === 'all') {
      return true
    }

    const minWidth = breakpoints[pluginFromBreakpoint]
    return viewportWidth >= minWidth
  }

  get carousel () {
    return this.querySelector('[carousel]')
  }

  get options () {
    try {
      const defaultOptions = { skipSnaps: true }

      if (!this.dataset.options) return defaultOptions

      const trimmedOptions = this.dataset.options.trim()

      if (!trimmedOptions || trimmedOptions === '{' || trimmedOptions === '}') {
        console.warn('CarouselComponent: Empty or incomplete JSON options, using defaults')
        return defaultOptions
      }

      const cleanedJson = trimmedOptions
        .replace(/\n\s*/g, ' ')
        .replace(/\s{2,}/g, ' ')

      const parsedOptions = JSON.parse(cleanedJson)
      return { ...defaultOptions, ...parsedOptions }
    } catch (e) {
      console.error('CarouselComponent: Invalid options format', e)
      console.error('Raw options data:', this.dataset.options)
      return { skipSnaps: true }
    }
  }

  get dotsContainer () {
    return this.querySelector('.carousel__dots') || this.querySelector('.carousel-dots')
  }

  get hasDots () {
    return this.dotsContainer !== null
  }

  get hasControls () {
    return this.querySelector('.carousel-controls') !== null
  }

  get pluginType () {
    return this.dataset.plugin || 'none'
  }

  get pluginFrom () {
    return this.dataset.pluginFrom || 'all' // Reads 'data-plugin-from' value
  }

  get pluginSpeedForAutoscroll () {
    const speed = this.dataset.pluginSpeed || 'slow'
    const speeds = {
      slow: 2,
      medium: 4,
      fast: 6,
    }
    return speeds[speed] || speeds.slow
  }

  get pluginSpeedForAutoplay () {
    const delay = this.dataset.pluginSpeed || 'medium'
    const delays = {
      slow: 5000, // 5 seconds
      medium: 3000, // 3 seconds
      fast: 1000, // 1 second
    }
    return delays[delay] || delays.medium
  }

  get animateNextOnShow () {
    return this.dataset.animateNextOnShow === 'true'
  }

  updateControlsVisibility () {
    if (this.carouselControls) {
      if (this.isCarouselActive()) {
        this.carouselControls.style.opacity = '1'
      } else {
        this.carouselControls.style.opacity = '0'
      }
    }
  }

  setupIntersectionObserver () {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasAnimatedNext && this.embla) {
          setTimeout(() => {
            this.embla.scrollNext()
            this.hasAnimatedNext = true
          }, 1500)
          this.intersectionObserver.disconnect()
        }
      })
    })

    this.intersectionObserver.observe(this)
  }

  updateArrowButtonsState () {
    if (!this.hasControls || !this.prevBtnEl || !this.nextBtnEl) return

    const canScrollPrev = this.embla ? this.embla.canScrollPrev() : false
    const selectedIndex = this.embla ? this.embla.selectedScrollSnap() : 0
    const totalSlides = this.embla ? this.embla.slideNodes().length : 0

    // Calculate how many slides are visible at once
    const container = this.embla ? this.embla.containerNode() : null
    const slides = this.embla ? this.embla.slideNodes() : []
    let slidesVisible = 1

    if (container && slides.length > 0) {
      const containerWidth = container.getBoundingClientRect().width
      const slideWidth = slides[0].getBoundingClientRect().width
      slidesVisible = Math.floor(containerWidth / slideWidth)
    }

    // The last meaningful slide is when the last slide is visible
    const lastMeaningfulIndex = Math.max(0, totalSlides - slidesVisible)

    let canScrollNext
    if (!this.embla) {
      canScrollNext = false
    } else if (this.options.loop === true) {
      canScrollNext = this.embla.canScrollNext()
    } else {
      canScrollNext = selectedIndex < lastMeaningfulIndex
    }

    const inactiveClass = 'carousel-controls__button--inactive'

    if (canScrollPrev) {
      this.prevBtnEl.removeAttribute('disabled')
      this.prevBtnEl.classList.remove(inactiveClass)
    } else {
      this.prevBtnEl.setAttribute('disabled', 'disabled')
      this.prevBtnEl.classList.add(inactiveClass)
    }

    if (canScrollNext) {
      this.nextBtnEl.removeAttribute('disabled')
      this.nextBtnEl.classList.remove(inactiveClass)
    } else {
      this.nextBtnEl.setAttribute('disabled', 'disabled')
      this.nextBtnEl.classList.add(inactiveClass)
    }
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent)
}
